// Individual Fan-session playback diagnostics (LSC-598). A small, best-effort recorder
// that ConsumerWatch feeds with the diagnostic signals it already tracks — connection /
// subscription transitions, track mute/replace, media errors/stalls, recovery attempts,
// audio-enable actions, page visibility, go-live, join/leave — into a BOUNDED ring buffer.
// When a fan hits intermittent trouble and taps "Report a problem", the buffer + an opaque
// per-session reference are posted to the server (submitPlaybackReport in api.ts), which
// returns a short reference the fan can quote to Ops.
//
// Design constraints (from the issue): never degrade playback or fail the session when
// telemetry is unavailable (every method is try/caught and silent), bound the volume, and
// carry NO secrets — only {t, kind, detail} entries, never tokens/cookies/URLs. This
// EXTENDS the existing analytics (ADR 0017) + Faro RUM (ADR 0086), not a parallel system.

export interface DiagEvent {
  /** ms since the watch session started. */
  t: number;
  /** a short, stable signal name, e.g. "conn:reconnecting", "audio:blocked", "source:lost". */
  kind: string;
  /** optional short, non-secret context. */
  detail?: string;
}

const RING_CAP = 80; // the most-recent window kept client-side (server also caps at 100)
const DETAIL_MAX = 240;

/** PlaybackDiagnostics is the per-watch-session recorder. One instance per Watch mount. */
export class PlaybackDiagnostics {
  readonly sessionRef: string;
  private readonly start = Date.now();
  private buf: DiagEvent[] = [];

  constructor(sessionRef?: string) {
    this.sessionRef = sessionRef && sessionRef.length > 0 ? sessionRef : randomRef();
  }

  /** Append a diagnostic event. Best-effort: never throws into the player. */
  record(kind: string, detail?: string): void {
    try {
      const e: DiagEvent = { t: Date.now() - this.start, kind: String(kind).slice(0, 48) };
      if (detail != null && detail !== "") e.detail = String(detail).slice(0, DETAIL_MAX);
      this.buf.push(e);
      if (this.buf.length > RING_CAP) this.buf.splice(0, this.buf.length - RING_CAP);
    } catch {
      /* diagnostics must never break playback */
    }
  }

  /** A copy of the current ring buffer (oldest → newest). */
  events(): DiagEvent[] {
    return this.buf.slice();
  }
}

/** A URL-unsafe-free opaque reference. crypto when available, Math.random fallback. */
function randomRef(): string {
  try {
    const a = new Uint8Array(8);
    (globalThis.crypto ?? (globalThis as unknown as { msCrypto?: Crypto }).msCrypto)?.getRandomValues(a);
    if (a.some((b) => b !== 0)) {
      return Array.from(a, (b) => b.toString(16).padStart(2, "0")).join("");
    }
  } catch {
    /* fall through */
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

/** sessionRefKey derives the per-(tab, broadcast) sessionStorage key so a report + a quick
 *  remount of the same broadcast correlate to one reference. */
function sessionRefKey(broadcastId: string): string {
  return `legends.diag.ref.${broadcastId}`;
}

/** Create (or restore) the diagnostics recorder for a broadcast. The session reference is
 *  stable per tab+broadcast via sessionStorage (best-effort — a blocked store just yields a
 *  fresh ref, which still works). */
export function createDiagnostics(broadcastId: string): PlaybackDiagnostics {
  let ref: string | undefined;
  try {
    ref = sessionStorage.getItem(sessionRefKey(broadcastId)) ?? undefined;
  } catch {
    /* private mode / blocked store */
  }
  const diag = new PlaybackDiagnostics(ref);
  if (!ref) {
    try {
      sessionStorage.setItem(sessionRefKey(broadcastId), diag.sessionRef);
    } catch {
      /* ignore */
    }
  }
  return diag;
}
